import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getEventsList, deleteEvent, setEventsPage, getEventsMinimal } from '../../actions/eventActions';
import { getPrograms } from '../../actions/programActions';
import ContentManagementSidebar from '../content-management/ContentManagementSidebar';
import TableView from '../content-management/views/TableView';
import MobileView from '../content-management/views/MobileView';
import TableSkeleton from '../content-management/views/TableView/TableSkeleton';
import MobileSkeleton from '../content-management/views/MobileView/MobileSkeleton';
import DeleteConfirmationModal from '../content-management/common/DeleteConfirmationModal';
import PaginationControls from '../common/PaginationControls';
import MultiSelectSearchFilter from '../content-management/common/MultiSelectSearchFilter';
import { Plus, Filter, X, Calendar } from 'lucide-react';
import { loadContentManagementFilters, saveContentManagementFilters } from '../../utils/contentManagementFilters';
import { isAdminRole } from '../../utils/roleHelpers';

const EventsManagementContainer = ({
  auth: { user },
  event: { events, minimalEvents, loading, currentPage, totalPages, totalEvents, hasNextPage, hasPrevPage },
  program: { programs },
  getEventsList,
  deleteEvent,
  getPrograms,
  setEventsPage,
  getEventsMinimal,
  hideSidebar
}) => {
  const navigate = useNavigate();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTitles, setSelectedTitles] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedPrograms, setSelectedPrograms] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filtersHydrated, setFiltersHydrated] = useState(false);
  const filtersLoadedRef = useRef(false);

  // Role detection
  const isAdmin = isAdminRole(user?.role);
  const isInstructor = user?.role === 'instructor';

  // Instructors have read-only access to events
  const isReadOnly = isInstructor;

  useEffect(() => {
    getPrograms();
    getEventsMinimal();
  }, [getPrograms, getEventsMinimal]);

  useEffect(() => {
    const saved = loadContentManagementFilters('Events & Information');
    if (saved?.filters) {
      setSelectedTitles(saved.filters.selectedTitles || []);
      setSelectedTypes(saved.filters.selectedTypes || []);
      setSelectedStatuses(saved.filters.selectedStatuses || []);
      setSelectedPrograms(saved.filters.selectedPrograms || []);
      setStartDate(saved.filters.startDate || '');
      setEndDate(saved.filters.endDate || '');
      if (saved.count > 0) {
        setShowFilters(true);
      }
    }
    filtersLoadedRef.current = true;
    setFiltersHydrated(true);
  }, []);

  useEffect(() => {
    if (!filtersHydrated) return;
    const filters = {};
    if (selectedTitles.length > 0) {
      filters.title = selectedTitles;
    }
    if (selectedTypes.length > 0) {
      filters.type = selectedTypes;
    }
    if (selectedStatuses.length > 0) {
      filters.status = selectedStatuses;
    }
    if (selectedPrograms.length > 0) {
      filters.program = selectedPrograms;
    }
    if (startDate) {
      filters.startDate = startDate;
    }
    if (endDate) {
      filters.endDate = endDate;
    }
    getEventsList(currentPage, 10, filters);
  }, [filtersHydrated, currentPage, selectedTitles, selectedTypes, selectedStatuses, selectedPrograms, startDate, endDate, getEventsList]);

  useEffect(() => {
    if (!filtersLoadedRef.current) return;
    const count = selectedTitles.length + selectedTypes.length + selectedStatuses.length + selectedPrograms.length + (startDate ? 1 : 0) + (endDate ? 1 : 0);
    saveContentManagementFilters('Events & Information', {
      selectedTitles,
      selectedTypes,
      selectedStatuses,
      selectedPrograms,
      startDate,
      endDate
    }, count);
  }, [selectedTitles, selectedTypes, selectedStatuses, selectedPrograms, startDate, endDate]);

  const handleAdd = () => {
    if (isReadOnly) return; // Prevent add in read-only mode
    navigate('/content-management/events/new');
  };

  const handleView = (item) => {
    navigate(`/content-management/events/${item._id}/view`);
  };

  const handleEdit = (item) => {
    if (isReadOnly) return; // Prevent edit in read-only mode
    navigate(`/content-management/events/${item._id}/edit`);
  };

  const handleDeleteClick = (item) => {
    if (isReadOnly) return; // Prevent delete in read-only mode
    setItemToDelete(item);
    setShowDeleteConfirmation(true);
  };

  const handleDeleteConfirm = () => {
    deleteEvent(itemToDelete._id);
    setShowDeleteConfirmation(false);
    setItemToDelete(null);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirmation(false);
    setItemToDelete(null);
  };

  const handleTabChange = (tabId) => {
    if (tabId === 'Events & Information') {
      // Already on events page, do nothing
      return;
    }
    // Navigate to content management with selected tab
    navigate('/content-management', { state: { activeTab: tabId } });
  };

  const getItemTitle = (item) => {
    return item.title;
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

    const getThumbnailUrl = (thumbnail) => {
    if (!thumbnail) return null;
    if (thumbnail.url) return thumbnail.url;
    if (thumbnail.data && thumbnail.contentType) return `data:${thumbnail.contentType};base64,${thumbnail.data}`;
    return null;
  };

  // Handle pagination - only update Redux state, useEffect will fetch data
  const handlePageChange = (newPage) => {
    setEventsPage(newPage);
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const clearAllFilters = () => {
    setSelectedTitles([]);
    setSelectedTypes([]);
    setSelectedStatuses([]);
    setSelectedPrograms([]);
    setStartDate('');
    setEndDate('');
    setEventsPage(1);
  };

  const hasActiveFilters = selectedTitles.length > 0 || selectedTypes.length > 0 || 
    selectedStatuses.length > 0 || selectedPrograms.length > 0 || startDate || endDate;

  // Get unique values from events
  const uniqueTitles = minimalEvents && minimalEvents.length > 0
    ? [...new Set(minimalEvents.map(e => e.title))].filter(Boolean)
    : [];
    
  const uniqueTypes = minimalEvents && minimalEvents.length > 0
    ? [...new Set(minimalEvents.map(e => e.type))].filter(Boolean)
    : [];
    
  // Provide static status options so both are always available for filtering
  const uniqueStatuses = ['Active', 'Deactivated'];
    
  // Check if any selected type is 'event' to show date filter
  const showDateFilter = selectedTypes.length === 0 || selectedTypes.some(type => type.toLowerCase() === 'event');

  const content = (
    <div className={hideSidebar ? '' : "flex-1 border-t md:border-t-0 md:border-l border-gray-200 p-6 min-w-0 overflow-x-auto"}>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold text-[#054581]">
              Manage Events
            </h2>
                {isReadOnly && (
                  <span className="px-3 py-1 bg-orange-100 text-orange-800 text-sm font-medium rounded-full">
                    Read Only
                  </span>
                )}
                {totalEvents > 0 && (
                  <span className="text-sm text-gray-500">
                    ({totalEvents} total)
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={toggleFilters}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                    hasActiveFilters
                      ? 'bg-[#1086a4] text-white shadow-md'
                      : 'bg-white border border-[#1086a4] text-[#054581] hover:bg-[#f9fafb]'
                  }`}
                >
                  <Filter size={18} />
                  <span>{showFilters ? 'Hide Filters' : 'Show Filters'}</span>
                  {hasActiveFilters && (
                    <span className="bg-white text-[#1086a4] text-xs px-2 py-0.5 rounded-full font-semibold">
                      Active
                    </span>
                  )}
                </button>
                {!isReadOnly && (
                  <button
                    onClick={handleAdd}
                    className="bg-[#1086a4] text-white px-2 sm:px-4 py-2 rounded-lg font-semibold shadow hover:bg-[#054581] transition-colors flex items-center gap-1 sm:gap-2 text-sm sm:text-base"
                  >
                    <Plus className="w-4 h-4 flex-shrink-0" />
                    <span>Add</span>
                  </button>
                )}
              </div>
            </div>

            {/* Filter UI */}
            {showFilters && (
              <div className="mb-6 space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-[#054581]">Filter Events</h3>
                  <button
                    onClick={clearAllFilters}
                    disabled={!hasActiveFilters}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                      hasActiveFilters
                        ? 'bg-red-500 hover:bg-red-600 text-white cursor-pointer'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    <X className="w-3 h-3" />
                    Clear All Filters
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <MultiSelectSearchFilter
                    items={uniqueTitles.map(title => ({ _id: title, title: title }))}
                    selectedItems={selectedTitles}
                    onSelectionChange={(selected) => {
                      setSelectedTitles(selected);
                      setEventsPage(1);
                    }}
                    title="Titles"
                    placeholder="Search titles..."
                    emptyMessage="No titles available"
                  />

                  <MultiSelectSearchFilter
                    items={uniqueTypes.map(type => ({ _id: type, title: type }))}
                    selectedItems={selectedTypes}
                    onSelectionChange={(selected) => {
                      setSelectedTypes(selected);
                      setEventsPage(1);
                    }}
                    title="Types"
                    placeholder="Search types..."
                    emptyMessage="No types available"
                  />

                  <MultiSelectSearchFilter
                    items={uniqueStatuses.map(status => ({ _id: status, title: status }))}
                    selectedItems={selectedStatuses}
                    onSelectionChange={(selected) => {
                      setSelectedStatuses(selected);
                      setEventsPage(1);
                    }}
                    title="Statuses"
                    placeholder="Search statuses..."
                    emptyMessage="No statuses available"
                  />

                  <MultiSelectSearchFilter
                    items={programs}
                    selectedItems={selectedPrograms}
                    onSelectionChange={(selected) => {
                      setSelectedPrograms(selected);
                      setEventsPage(1);
                    }}
                    title="Programs"
                    placeholder="Search programs..."
                    emptyMessage="No programs available"
                  />
                </div>

                {/* Date Range Filter - Only for Events */}
                {showDateFilter && (
                  <div className="bg-gradient-to-r from-gray-50 to-blue-50 p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2 mb-3">
                      <Calendar className="w-4 h-4 text-[#1086a4]" />
                      <span className="text-sm font-medium text-[#054581]">Event Date Range</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">From</label>
                        <input
                          type="date"
                          value={startDate}
                          onChange={(e) => {
                            setStartDate(e.target.value);
                            setEventsPage(1);
                          }}
                          className="w-full px-3 py-2 text-sm text-gray-900 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1086a4]"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">To</label>
                        <input
                          type="date"
                          value={endDate}
                          onChange={(e) => {
                            setEndDate(e.target.value);
                            setEventsPage(1);
                          }}
                          className="w-full px-3 py-2 text-sm text-gray-900 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1086a4]"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {loading ? (
              <>
                {/* Desktop Skeleton */}
                <div className="hidden md:block">
                  <TableSkeleton rowCount={10} activeTab="Events" />
                </div>

                {/* Mobile Skeleton */}
                <div className="md:hidden">
                  <MobileSkeleton cardCount={10} />
                </div>
              </>
            ) : (
              <>
                {/* Mobile View */}
                <div className="md:hidden">
                  <MobileView
                    items={events}
                    activeTab="Events & Information"
                    onView={handleView}
                    onEdit={handleEdit}
                    onDelete={handleDeleteClick}
                    getThumbnailUrl={getThumbnailUrl}
                    truncateText={truncateText}
                    getItemTitle={getItemTitle}
                    isReadOnly={isReadOnly}
                  />
                </div>

                {/* Desktop View */}
                <div className="hidden md:block">
                  <TableView
                    items={events}
                    activeTab="Events & Information"
                    onView={handleView}
                    onEdit={handleEdit}
                    onDelete={handleDeleteClick}
                    getThumbnailUrl={getThumbnailUrl}
                    truncateText={truncateText}
                    getItemTitle={getItemTitle}
                    isReadOnly={isReadOnly}
                  />
                </div>

                {/* Pagination Controls */}
                <PaginationControls
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalEvents}
                  itemsPerPage={10}
                  onPageChange={handlePageChange}
                  hasNextPage={hasNextPage}
                  hasPrevPage={hasPrevPage}
                  itemName="events"
                />
              </>
            )}
      </div>
    </div>
  );

  const deleteModal = (
    <DeleteConfirmationModal
      isOpen={showDeleteConfirmation}
      onClose={handleDeleteCancel}
      onConfirm={handleDeleteConfirm}
      itemTitle={itemToDelete ? getItemTitle(itemToDelete) : ''}
      itemType="Event"
    />
  );

  if (hideSidebar) {
    return <>{content}{deleteModal}</>;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 px-2 sm:py-8 sm:px-4 pb-20 md:pb-4">
      <div className="mx-auto bg-white rounded-lg shadow-sm flex flex-col md:flex-row max-w-full overflow-hidden">
        <ContentManagementSidebar
          activeTab="Events & Information"
          onTabChange={handleTabChange}
          isAdmin={isAdmin}
          isInstructor={isInstructor}
        />
        {content}
        {deleteModal}
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
  event: state.event,
  program: state.program
});

export default connect(
  mapStateToProps,
  {
    getEventsList,
    deleteEvent,
    getPrograms,
    setEventsPage,
    getEventsMinimal
  }
)(EventsManagementContainer);
